import { useNodeManager } from '@/composables/useNodeManager';
import {
  MAX_SCALE,
  MAX_SCALE_X,
  MAX_SCALE_Y,
  MAX_SCALE_Z,
  MIN_SCALE,
  MIN_SCALE_X,
  MIN_SCALE_Y,
  MIN_SCALE_Z,
} from '@/constants/appConstant';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { camera, cameraControls, render, renderer, scene } from '@/threejs/core';
import { edgesManager } from '@/threejs/managers/edgesManager';
import { getObjectByCode, updateObject } from '@/threejs/objectManager';
import { buildLookAtQuaternion } from '@/utils/projectorCalculations';
import * as THREE from 'three';
import { watch } from 'vue';
import { TransformControls } from './TransformControls';

export let transformControls;
let _onCameraChanged = null;
let isTransformingTarget = false;
let savedProjectorUpVector = null;
let savedLedTileCounts = null;
let _watchStopHandles = [];
let _onChange = null;
let _onDraggingChanged = null;
let _onMouseDown = null;
let _onMouseUp = null;
let _onObjectChange = null;

// Snapping configuration for projector target
const SNAP_ENABLED = true; // Enable/disable snapping
const SNAP_DISTANCE = 5.0; // Maximum distance to snap (in world units)
const snapRaycaster = new THREE.Raycaster();

// Projector target world position locking
// When true (default), moving the projector keeps the target's world position fixed
// and updates the throw distance accordingly
const KEEP_TARGET_WORLD_POSITION = true;

let localTargetPoint = new THREE.Vector3();

// All per-drag transient state grouped into a single object.
// Created on mouseDown, nulled on mouseUp — prevents leaking individual fields.
let dragSession = null;

// Reusable objects for roll extraction during rotation gizmo drag (avoids per-frame allocation)
const _rollFwd = new THREE.Vector3();
const _rollDefaultUp = new THREE.Vector3();
const _rollActualUp = new THREE.Vector3();
const _rollCross = new THREE.Vector3();

const safeScale = (val, fallback = 1) => {
  return isNaN(val) || val === null ? fallback : val;
};

/**
 * Update visibility of all projector and target sprites based on transform mode
 * @param {boolean} hideSprites - Whether to hide sprites (true when in transform mode)
 */
function updateAllSpritesVisibility(hideSprites) {
  // Check global viewport settings to see if sprites are enabled
  const cruxModuleStore = useCruxModuleStore();
  const globalSpritesEnabled = cruxModuleStore.getSettingValue('viewport_settings', 'render.showProjectorSprite', true);

  // Only show sprites if both conditions are met:
  // 1. Transform mode allows it (!hideSprites)
  // 2. Global setting has sprites enabled
  const shouldShowSprites = !hideSprites && globalSpritesEnabled;

  scene.traverse((object) => {
    if (object.userData.type === 'projector' && object.projectorHelpers) {
      object.projectorHelpers.updateProjectorSpriteVisibility(shouldShowSprites);
      object.projectorHelpers.updateTargetSpriteVisibility(shouldShowSprites);
    }
  });
}

function isScaleDisabledForType(mesh) {
  return mesh.userData.isProjector;
}

function setScaleGizmoMode(mesh, transformControls) {
  if (mesh.userData.lock_scale) {
    transformControls.showX = true;
    transformControls.showY = false;
    transformControls.showZ = false;
    transformControls.axis = 'XYZ';
  } else {
    transformControls.showX = true;
    transformControls.showY = true;
    transformControls.showZ = true;
  }
}

/**
 * Attempts to snap the target position to a mesh surface near the target
 * Respects the transform control's axis constraints
 * @param {THREE.Vector3} transformPosition - The position calculated by transform controls
 * @param {string} targetCode - The code/uuid of the target being transformed
 * @param {string} axis - The current transform axis ('X', 'Y', 'Z', 'XY', 'XZ', 'YZ', 'XYZ', etc.)
 * @returns {THREE.Vector3} - The snapped position or original if no snap found
 */
function trySnapToSurface(transformPosition, targetCode, axis) {
  if (!SNAP_ENABLED || !axis) return transformPosition;

  // Collect all selectable meshes in the scene (excluding the target itself)
  const snapTargets = [];
  scene.traverse((object) => {
    // Only include meshes that are selectable and not the transform target
    if (
      object.isMesh &&
      object.userData.selectable === true &&
      !object.userData.isTarget &&
      !object.userData.isProjectorTarget &&
      !object.userData.isAnnotationTarget &&
      object.visible
    ) {
      snapTargets.push(object);
    }
  });

  if (snapTargets.length === 0) return transformPosition;

  // Cast ray from camera through the target's current position
  const targetScreenPos = transformPosition.clone().project(camera);
  snapRaycaster.setFromCamera(targetScreenPos, camera);
  snapRaycaster.far = 200.0; // Cast far to find surfaces

  const intersects = snapRaycaster.intersectObjects(snapTargets, true);

  // If we hit a surface along the ray through the target
  if (intersects.length > 0) {
    const hitPoint = intersects[0].point;

    // Check if hit point is within snap distance
    const distance = transformPosition.distanceTo(hitPoint);

    if (distance < SNAP_DISTANCE) {
      // Calculate the snapped position based on active axes
      const snappedPosition = transformPosition.clone();

      // Only snap along the active axes
      const activeX = axis.includes('X');
      const activeY = axis.includes('Y');
      const activeZ = axis.includes('Z');

      // Snap to hit point along active axes
      if (activeX) {
        snappedPosition.x = hitPoint.x;
      }
      if (activeY) {
        snappedPosition.y = hitPoint.y;
      }
      if (activeZ) {
        snappedPosition.z = hitPoint.z;
      }

      return snappedPosition;
    }
  }

  return transformPosition;
}

function updateScale(object, payload, isFinal = false) {
  // Clamp scale for all objects
  if (object.userData.lock_scale === true) {
    const uniformScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, safeScale(object.scale.x)));
    object.scale.set(uniformScale, uniformScale, uniformScale);
    payload.scale = uniformScale;
  } else {
    object.scale.x = Math.max(MIN_SCALE_X, Math.min(MAX_SCALE_X, safeScale(object.scale.x)));
    object.scale.y = Math.max(MIN_SCALE_Y, Math.min(MAX_SCALE_Y, safeScale(object.scale.y)));
    object.scale.z = Math.max(MIN_SCALE_Z, Math.min(MAX_SCALE_Z, safeScale(object.scale.z)));
    payload.scale = { x: object.scale.x, y: object.scale.y, z: object.scale.z };
  }

  // LED-specific: convert scale to tile counts
  if (object.userData.type === 'led_screen' && savedLedTileCounts) {
    const scaleX = safeScale(object.scale.x);
    const scaleY = safeScale(object.scale.y);

    const origH = savedLedTileCounts.horizontalSize;
    const origV = savedLedTileCounts.verticalSize;
    const tileWidth = origH > 0 ? savedLedTileCounts.totalWidth / origH : 0;
    const tileHeight = origV > 0 ? savedLedTileCounts.totalHeight / origV : 0;

    if (tileWidth > 0 && tileHeight > 0) {
      const newH = Math.max(1, Math.round(origH * scaleX));
      const newV = Math.max(1, Math.round(origV * scaleY));

      payload.horizontalSize = newH;
      payload.verticalSize = newV;
      payload.totalWidth = tileWidth * newH;
      payload.totalHeight = tileHeight * newV;
    }

    // On mouseUp: reset LED scale and clear saved state
    if (isFinal) {
      object.scale.set(1, 1, 1);
      payload.scale = { x: 1, y: 1, z: 1 };
      savedLedTileCounts = null;
    }
  }
}
export function setUpTransformControls(sceneParam, cameraParam, cameraControlsParam) {
  const selectionStore = useSelectionStore();
  const { updateNode } = useNodeManager();
  const cruxModuleStore = useCruxModuleStore();

  // Use passed camera or fall back to imported camera
  const activeCamera = cameraParam || camera;
  transformControls = new TransformControls(activeCamera, renderer.domElement);
  transformControls.size = 0.75;
  transformControls.getRaycaster().layers.set(1);
  _onChange = () => render();
  transformControls.addEventListener('change', _onChange);

  _onDraggingChanged = (event) => {
    cameraControls.enabled = !event.value;
  };
  transformControls.addEventListener('dragging-changed', _onDraggingChanged);

  _onMouseDown = function (event) {
    cruxModuleStore.pause();

    transformControls.object.isTransforming = true;

    // Save LED tile counts at drag start for live scaling
    if (transformControls.object.userData.type === 'led_screen') {
      const ud = transformControls.object.userData;
      savedLedTileCounts = {
        horizontalSize: ud.horizontalSize || 1,
        verticalSize: ud.verticalSize || 1,
        totalWidth: ud.totalWidth || 0,
        totalHeight: ud.totalHeight || 0,
      };
    }

    // Initialize drag session — all per-drag transient state in one place
    dragSession = {
      ledZScale: null,
      projectorThrowDist: null,
      targetDistance: null,
      projectorTargetStart: null,
      projectorPositionStart: null,
    };

    if (transformControls.object.userData.isProjectorTarget) {
      const projectorObject = getObjectByCode(transformControls.object.userData.projectorCode);
      projectorObject.isTransforming = true;
      // Save throw distance from lens position
      const lensPos = projectorObject.getLensWorldPosition();
      dragSession.targetDistance = lensPos.distanceTo(transformControls.object.position);
    }

    if (transformControls.object && transformControls.object.userData.isProjector) {
      transformControls.object.updateMatrixWorld(true);

      localTargetPoint = transformControls.object.userData.target.clone();
      transformControls.object.worldToLocal(localTargetPoint);

      // Save throw distance from lens to target at drag start
      const lensWorld = transformControls.object.getLensWorldPosition();
      dragSession.projectorThrowDist = lensWorld.distanceTo(transformControls.object.userData.target);

      // Save the world-space up vector to preserve roll during drag
      savedProjectorUpVector = new THREE.Vector3(0, 1, 0);
      savedProjectorUpVector.applyQuaternion(transformControls.object.quaternion).normalize();

      // Save initial positions for delta-based target movement during translate drag
      dragSession.projectorTargetStart = transformControls.object.userData.target.clone();
      dragSession.projectorPositionStart = transformControls.object.position.clone();
    } else {
      // Clear saved up vector if not transforming a projector
      savedProjectorUpVector = null;
    }

    // Save Z scale for LED screens to prevent Z scaling
    if (transformControls.object.userData.type === 'led_screen' && transformControls.mode === 'scale') {
      dragSession.ledZScale = transformControls.object.scale.z;
    }

    // Save initial position, quaternion, and child positions for groups
    if (transformControls.object?.userData?.type === 'group') {
      transformControls.object.userData._prevPos = transformControls.object.position.clone();
      transformControls.object.userData._prevQuat = transformControls.object.quaternion.clone();
      // Save true original child positions for scale counter (undo any existing counter-scale)
      const childOrigPositions = {};
      transformControls.object.traverse((child) => {
        if (child !== transformControls.object && child.userData?.isProjector) {
          // Use the saved original position if available, otherwise reconstruct from current position * current scale
          childOrigPositions[child.userData.code] = child.userData._origLocalPos
            ? child.userData._origLocalPos.clone()
            : child.position.clone().multiply(transformControls.object.scale);
        }
      });
      transformControls.object.userData._childOrigPositions = childOrigPositions;
    }
  };
  transformControls.addEventListener('mouseDown', _onMouseDown);

  // Listen for camera changes and update RenderPass
  _onCameraChanged = (event) => {
    const newCamera = event.detail.camera;
    transformControls.camera = newCamera;
  };
  window.addEventListener('cameraChanged', _onCameraChanged);

  _onMouseUp = function (event) {
    const object = transformControls.object;

    cruxModuleStore.resume();
    cruxModuleStore.commit();

    if (object.userData.isProjectorTarget) {
      const projectorObject = getObjectByCode(object.userData.projectorCode);
      const updatedTarget = object.position.clone();
      projectorObject.userData.target.copy(updatedTarget);

      updateNode(
        object.userData.projectorCode,
        {
          target: {
            x: updatedTarget.x,
            y: updatedTarget.y,
            z: updatedTarget.z,
          },
          rotation: {
            x: projectorObject.rotation.x * (180 / Math.PI),
            y: projectorObject.rotation.y * (180 / Math.PI),
            z: (projectorObject.userData.roll || 0) * (180 / Math.PI),
          },
        },
        true
      );

      // Set isTransforming false AFTER updateNode so applyProperties doesn't override the centered target
      projectorObject.isTransforming = false;
      object.isTransforming = false;
      return;
    }

    const updatedWorldPoint = localTargetPoint.clone();
    object.localToWorld(updatedWorldPoint);
    const payload = {
      position: {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      },
      rotation: {
        x: object.rotation.x * (180 / Math.PI),
        y: object.rotation.y * (180 / Math.PI),
        z: object.userData.isProjector
          ? (object.userData.roll || 0) * (180 / Math.PI)
          : object.rotation.z * (180 / Math.PI),
      },
    };

    if (transformControls.mode === 'scale') {
      // Restore Z scale for LED screens (Z axis inactive)
      if (dragSession?.ledZScale !== null) {
        object.scale.z = dragSession.ledZScale;
      }
      // Check zoom before updateScale resets LED scale to 1
      updateScale(object, payload, object.userData.type === 'led_screen');
    }
    updateNode(object.userData.code, payload, true);

    if (object.userData.isProjector && object.userData.lockTarget !== true) {
      // Use the target that was already computed during the last drag frame
      // Don't recompute from gizmo quaternion to avoid makeBasis mismatch
      const target = object.userData.target;
      object.rotateToTarget(target);

      const targetMesh = getObjectByCode(object.userData.code + '-target');
      if (targetMesh) targetMesh.position.copy(target);

      updateNode(
        object.userData.code,
        {
          target: {
            x: target.x,
            y: target.y,
            z: target.z,
          },
        },
        true
      );
    }

    // For groups: persist child projector targets (they were updated during drag while tracking was paused)
    if (object.userData.type === 'group') {
      object.traverse((child) => {
        // Skip clones - only persist original projector targets
        if (child.userData?.isProjector && child.userData.target && !child.userData.clone) {
          updateNode(
            child.userData.code,
            {
              target: { x: child.userData.target.x, y: child.userData.target.y, z: child.userData.target.z },
            },
            true
          );
        }
      });
      delete object.userData._prevPos;
      delete object.userData._prevQuat;
      delete object.userData._childOrigPositions;
    }

    // Set isTransforming false AFTER all updates so applyProperties doesn't override
    object.isTransforming = false;

    // Clear saved state after transform is complete
    savedProjectorUpVector = null;
    dragSession = null;
  };
  transformControls.addEventListener('mouseUp', _onMouseUp);

  _onObjectChange = function (event) {
    const object = event.target.object;

    // Rebuild edge outlines to follow the object during drag
    edgesManager.refreshFlb();

    if (object.userData.isProjectorTarget) {
      const projectorObject = getObjectByCode(object.userData.projectorCode);
      projectorObject.updateMatrixWorld(true);

      const updatedWorldPoint = object.position.clone();
      projectorObject.rotateToTarget(updatedWorldPoint);
      projectorObject.userData.target.copy(updatedWorldPoint);
      const nodeUpdate = {
        rotation: {
          x: projectorObject.rotation.x * (180 / Math.PI),
          y: projectorObject.rotation.y * (180 / Math.PI),
          z: (projectorObject.userData.roll || 0) * (180 / Math.PI),
        },
        target: {
          x: updatedWorldPoint.x,
          y: updatedWorldPoint.y,
          z: updatedWorldPoint.z,
        },
      };
      updateNode(object.userData.projectorCode, nodeUpdate);

      render();
      return;
    }
    const updatedWorldPoint = localTargetPoint.clone();
    object.localToWorld(updatedWorldPoint);

    // Recalculate rotation BEFORE building the payload so it contains correct values
    if (object.userData.isProjector && object.userData.lockTarget === true) {
      // Locked target: use rotateToTarget() for canonical rotation matching mouseUp behavior
      // This prevents frustum bending during drag
      object.rotateToTarget(object.userData.target);
      object.updateMatrixWorld(true);
      localTargetPoint = object.userData.target.clone();
      object.worldToLocal(localTargetPoint);
    } else if (object.userData.isProjector && savedProjectorUpVector && transformControls.mode === 'translate') {
      // Unlocked target: preserve saved up vector to prevent roll drift during translate drag
      const targetPos = object.userData.target.clone();
      const lensWorld = object.getLensWorldPosition();
      const directionToTarget = new THREE.Vector3().subVectors(targetPos, lensWorld);

      if (directionToTarget.lengthSq() > 0) {
        directionToTarget.normalize();
        const defaultRight = new THREE.Vector3().crossVectors(
          new THREE.Vector3(0, 1, 0),
          directionToTarget.clone().negate()
        );
        if (defaultRight.lengthSq() > 0.000001) defaultRight.normalize();
        else defaultRight.set(1, 0, 0);

        const result = buildLookAtQuaternion(directionToTarget, {
          up: savedProjectorUpVector,
          degenerateRight: defaultRight,
        });
        object.quaternion.copy(result.quaternion);
        savedProjectorUpVector.copy(result.up);

        // Re-encode localTargetPoint with the new quaternion so the next frame
        // computes the correct world position (prevents distance shrink)
        object.updateMatrixWorld(true);
        localTargetPoint = object.userData.target.clone();
        object.worldToLocal(localTargetPoint);
      }
    }

    const payload = {
      position: {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      },
      rotation: {
        x: object.rotation.x * (180 / Math.PI),
        y: object.rotation.y * (180 / Math.PI),
        z: object.userData.isProjector
          ? (object.userData.roll || 0) * (180 / Math.PI)
          : object.rotation.z * (180 / Math.PI),
      },
    };

    if (transformControls.mode === 'scale') {
      // Restore Z scale for LED screens (Z axis inactive)
      if (dragSession?.ledZScale !== null) {
        object.scale.z = dragSession?.ledZScale;
      }
      updateScale(object, payload);
    }

    updateNode(object.userData.code, payload, true);

    // For groups: move/rotate child projectors' targets with the group during drag
    if (object.userData.type === 'group' && object.userData._prevPos) {
      const prevPos = object.userData._prevPos;
      const prevQuat = object.userData._prevQuat || object.quaternion.clone();
      const curPos = object.position.clone();
      const curQuat = object.quaternion.clone();
      const hasChange = prevPos.distanceToSquared(curPos) > 0 || !curQuat.equals(prevQuat);

      if (hasChange) {
        const rotDelta = curQuat.clone().multiply(prevQuat.clone().invert());
        object.traverse((child) => {
          if (child.userData?.isProjector) {
            // Compute offset from old group center, rotate by delta, add to new group center
            const offset = child.userData.target.clone().sub(prevPos);
            offset.applyQuaternion(rotDelta);
            child.userData.target.copy(curPos.clone().add(offset));
            child.rotateToTarget(child.userData.target);
            const targetMesh = getObjectByCode(child.userData.code + '-target');
            if (targetMesh) targetMesh.position.copy(child.userData.target);
            updateNode(child.userData.code, {
              target: { x: child.userData.target.x, y: child.userData.target.y, z: child.userData.target.z },
            });
          }
        });
      }
      object.userData._prevPos = curPos;
      object.userData._prevQuat = curQuat;
    }

    // Counter-scale projectors during gizmo drag (size + spacing)
    if (object.userData.type === 'group') {
      const gs = object.scale;
      const origPositions = object.userData._childOrigPositions || {};
      if (gs.x !== 0 && gs.y !== 0 && gs.z !== 0) {
        object.traverse((child) => {
          if (child !== object && child.userData?.isProjector) {
            child.scale.set(1 / gs.x, 1 / gs.y, 1 / gs.z);
            const origPos = origPositions[child.userData.code];
            if (origPos) {
              child.position.set(origPos.x / gs.x, origPos.y / gs.y, origPos.z / gs.z);
            }
          }
        });
      }
    }

    if (object.userData.isProjector && object.userData.lockTarget !== true) {
      let newTarget;

      if (
        transformControls.mode === 'translate' &&
        dragSession?.projectorTargetStart &&
        dragSession?.projectorPositionStart
      ) {
        // Pure translation: target moves by same delta as position
        const delta = object.position.clone().sub(dragSession?.projectorPositionStart);
        newTarget = dragSession?.projectorTargetStart.clone().add(delta);
      } else {
        // Rotation: recompute target from forward vector
        const lensWorld = object.getLensWorldPosition();
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(object.quaternion);
        const throwDist = dragSession?.projectorThrowDist ?? lensWorld.distanceTo(object.userData.target);
        newTarget = lensWorld.clone().add(forward.multiplyScalar(throwDist));
      }

      object.userData.target.copy(newTarget);
      updateNode(object.userData.code, { target: { x: newTarget.x, y: newTarget.y, z: newTarget.z } });
      const targetMesh = getObjectByCode(object.userData.code + '-target');
      if (targetMesh) targetMesh.position.copy(newTarget);
      render();
    }

    // When lockTarget is true, re-orient projector to face the fixed target during drag
    if (object.userData.isProjector && object.userData.lockTarget === true) {
      object.rotateToTarget(object.userData.target);
      object.updateMatrixWorld(true);
      localTargetPoint = object.userData.target.clone();
      object.worldToLocal(localTargetPoint);
    }

    // During rotation gizmo drag, extract roll from the quaternion so the frustum updates
    if (object.userData.isProjector && transformControls.mode === 'rotate') {
      _rollFwd.set(0, 0, -1).applyQuaternion(object.quaternion);
      const { quaternion: noRollQuat } = buildLookAtQuaternion(_rollFwd);
      _rollDefaultUp.set(0, 1, 0).applyQuaternion(noRollQuat);
      _rollActualUp.set(0, 1, 0).applyQuaternion(object.quaternion);
      const dot = Math.max(-1, Math.min(1, _rollDefaultUp.dot(_rollActualUp)));
      _rollCross.crossVectors(_rollDefaultUp, _rollActualUp);
      const sign = _rollCross.dot(_rollFwd) >= 0 ? -1 : 1;
      object.userData.roll = sign * Math.acos(dot);
    }

    if (
      object.userData.isProjector &&
      savedProjectorUpVector &&
      object.userData.lockTarget !== true &&
      transformControls.mode === 'translate'
    ) {
      // Restore the saved up vector to preserve roll during translate drag
      const targetPos = object.userData.target.clone();
      const lensWorld = object.getLensWorldPosition();
      const directionToTarget = new THREE.Vector3().subVectors(targetPos, lensWorld);

      if (directionToTarget.lengthSq() > 0) {
        directionToTarget.normalize();
        const defaultRight = new THREE.Vector3().crossVectors(
          new THREE.Vector3(0, 1, 0),
          directionToTarget.clone().negate()
        );
        if (defaultRight.lengthSq() > 0.000001) defaultRight.normalize();
        else defaultRight.set(1, 0, 0);

        const result = buildLookAtQuaternion(directionToTarget, {
          up: savedProjectorUpVector,
          degenerateRight: defaultRight,
        });
        object.quaternion.copy(result.quaternion);
        savedProjectorUpVector.copy(result.up);

        // Re-encode localTargetPoint with the new quaternion so the next frame
        // computes the correct world position (prevents distance shrink)
        object.updateMatrixWorld(true);
        localTargetPoint = object.userData.target.clone();
        object.worldToLocal(localTargetPoint);
      }
    }

    return;

    // Handle projector target2 (draggable gizmo)
    if (object.userData.isProjectorTarget && isTransformingTarget) {
      // Get the current axis from transform controls
      const currentAxis = transformControls.axis || 'XYZ';

      // Apply snapping if enabled and transforming a target
      let snappedPosition = object.position.clone();

      if (transformControls.dragging) {
        // Pass the transform-calculated position and current axis for snapping
        snappedPosition = trySnapToSurface(object.position, object.userData.forUuid, currentAxis);

        // Update object position to snapped position
        object.position.copy(snappedPosition);

        // Get the projector/spotlight object
        const spotLight = getObjectByCode(object.userData.forUuid);
        if (spotLight && spotLight.userData.type == 'projector' && spotLight.target2) {
          // Calculate the direction from spotlight to target2
          const direction = new THREE.Vector3().subVectors(snappedPosition, spotLight.position);

          // Calculate the distance
          const distance = direction.length();

          // Update target position (local to spotlight)
          spotLight.target.position.set(0, 0, -distance);

          // Make spotlight look at target2 position
          spotLight.lookAt(snappedPosition);

          // Copy spotlight's rotation back to target2 so local transform mode stays aligned
          spotLight.target2.quaternion.copy(spotLight.quaternion);

          // Update texture rotation
          if (spotLight.projectorTexture) {
            spotLight.projectorTexture.updateTextureRotation(spotLight.rotation);
          }

          // Update helper lines
          if (spotLight.projectorHelpers) {
            spotLight.projectorHelpers.updateHelperLines();
          }
        }
      }
    } else if (object.userData.isTarget) {
      // Handle other target types (not projector target2)
      // Get the current axis from transform controls
      const currentAxis = transformControls.axis || 'XYZ';

      // Apply snapping if enabled and transforming a target
      let snappedPosition = object.position.clone();

      if (isTransformingTarget && transformControls.dragging) {
        // Pass the transform-calculated position and current axis for snapping
        snappedPosition = trySnapToSurface(object.position, object.userData.forUuid, currentAxis);

        const targetObject = getObjectByCode(object.userData.forUuid);
        targetObject.lookAt(snappedPosition);

        targetObject.applyProperties({
          rotation: {
            x: targetObject.rotation.x * (180 / Math.PI),
            y: targetObject.rotation.y * (180 / Math.PI),
            z: targetObject.rotation.z * (180 / Math.PI),
          },
        });
      }
    } else {
      if (object && object.userData.isProjector) {
        const updatedWorldPoint = localTargetPoint.clone();
        object.localToWorld(updatedWorldPoint);

        updateNode(object.userData.code, {
          position: {
            x: object.position.x,
            y: object.position.y,
            z: object.position.z,
          },
          target: {
            x: updatedWorldPoint.x,
            y: updatedWorldPoint.y,
            z: updatedWorldPoint.z,
          },
          rotation: {
            x: object.rotation.x * (180 / Math.PI),
            y: object.rotation.y * (180 / Math.PI),
            z: object.rotation.z * (180 / Math.PI),
          },
        });
      }

      return;
      // For projectors with world position locking, handle rotation separately
      // Only apply locking when in translate mode, not rotate mode
      const isProjectorWithLocking =
        object.userData.isProjector &&
        object.target2 &&
        object.target &&
        KEEP_TARGET_WORLD_POSITION &&
        transformControls.mode === 'translate';

      // Save target2 position before applyProperties if locking is enabled
      let savedTarget2Position = null;
      if (isProjectorWithLocking) {
        savedTarget2Position = object.target2.position.clone();
      }

      const properties = {
        position: {
          x: object.position.x,
          y: object.position.y,
          z: object.position.z,
        },
      };

      // Only apply rotation if not a projector with world position locking
      if (!isProjectorWithLocking) {
        properties.rotation = {
          x: object.rotation.x * (180 / Math.PI),
          y: object.rotation.y * (180 / Math.PI),
          z: object.rotation.z * (180 / Math.PI),
        };
      }

      object.applyProperties(properties);

      // Restore target2 position after applyProperties if locking is enabled
      if (isProjectorWithLocking && savedTarget2Position) {
        object.target2.position.copy(savedTarget2Position);
      }
      if (object.userData.isProjector) {
        // Update target2 position and rotation when spotlight moves or rotates
        if (object.target2 && object.target) {
          if (KEEP_TARGET_WORLD_POSITION && transformControls.mode === 'translate') {
            // Keep target2's world position fixed, update target's local position
            // and throw distance based on new projector position

            // Calculate direction from projector to fixed target2 position
            const direction = new THREE.Vector3().subVectors(object.target2.position, object.position);
            const distance = direction.length();

            object.userData.lockTarget2 = true;
            // Update target's local position (in spotlight's local space)
            // Normalize direction and apply inverse of spotlight rotation
            direction.normalize();
            const localDirection = direction.clone();

            localDirection.applyQuaternion(object.quaternion.clone().invert());
            //object.target.position.copy(localDirection.multiplyScalar(distance))

            // Make spotlight look at the fixed target2 position
            // object.lookAt(object.target2.position)

            // Update target2 rotation to match spotlight
            //object.target2.quaternion.copy(object.quaternion)

            object.userData.lockTarget2 = false;

            // Update texture rotation
            if (object.projectorTexture) {
              object.projectorTexture.updateTextureRotation(object.rotation);
            }

            // Update helper lines to reflect new throw distance
            if (object.projectorHelpers) {
              object.projectorHelpers.updateHelperLines();
            }
          } else {
            // Original behavior: target2 follows projector, maintaining direction and distance
            const distance = object.target.position.length() || 73;
            const direction = new THREE.Vector3(0, 0, -1);
            direction.applyQuaternion(object.quaternion);
            object.target2.position.copy(object.position).add(direction.multiplyScalar(distance));

            // Copy rotation so local transform mode aligns with spotlight
            object.target2.quaternion.copy(object.quaternion);

            // Update texture rotation
            if (object.projectorTexture) {
              object.projectorTexture.updateTextureRotation(object.rotation);
            }
          }
        } else if (object.projectorTexture) {
          // Update texture rotation even if no target2
          object.projectorTexture.updateTextureRotation(object.rotation);
        }
      }
      /*
      updateNode(object.userData.code, {
        position: {
          x: object.position.x,
          y: object.position.y,
          z: object.position.z
        }
      })
        */
      updateObject(object.userData.code, {
        data: {
          position: {
            x: object.position.x,
            y: object.position.y,
            z: object.position.z,
          },
        },
      });
    }
  };
  transformControls.addEventListener('objectChange', _onObjectChange);

  _watchStopHandles.push(
    watch(
      () => {
        const selectedId = selectionStore.selectedObjectId;
        if (!selectedId) return null;
        const module = cruxModuleStore.cruxModules.find((m) => m.code === selectedId);
        return module ? module.data.lock_scale : null;
      },
      (newVal, oldVal) => {
        const selectedId = selectionStore.selectedObjectId;
        const mesh = getObjectByCode(selectedId);
        if (!mesh) return;
        mesh.userData.lock_scale = newVal;
        if (transformControls && transformControls.mode === 'scale') {
          changeTransformControlsAll(transformControls.mode);
        }
      }
    )
  );

  // Detach/reattach gizmo when the selected object's visibility changes
  _watchStopHandles.push(
    watch(
      () => {
        const selectedId = selectionStore.selectedObjectId;
        if (!selectedId) return null;
        const module = cruxModuleStore.cruxModules.find((m) => m.code === selectedId);
        return module ? module.data.visible : null;
      },
      (isVisible) => {
        const selectedId = selectionStore.selectedObjectId;
        if (!selectedId || !transformControls) return;

        const mesh = getObjectByCode(selectedId);
        if (!mesh) return;

        if (isVisible === false) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          // Clear selection outline immediately — don't wait for mesh.visible sync
          edgesManager.hideEdgesForSelected(null);
        } else if (isVisible === true) {
          // Restore selection outline
          edgesManager.hideEdgesForSelected(selectedId);
          if (selectionStore.selectedObjectTransformType) {
            const targetMesh = getObjectByCode(selectedId + '-target');
            if (selectionStore.selectedObjectTransformTarget && targetMesh) {
              transformControls.attach(targetMesh);
            } else {
              transformControls.attach(mesh);
            }
            updateAllSpritesVisibility(true);
          }
        }
        render();
      }
    )
  );

  _watchStopHandles.push(
    watch([() => selectionStore.selectedObjectId], ([newObject]) => {
      // Hide edge outlines on selected object (OutlinePass provides selection outline)
      edgesManager.hideEdgesForSelected(newObject || null);

      if (newObject) {
        const mesh = getObjectByCode(newObject);
        if (!mesh) return;

        // Don't attach transform controls to measurements
        if (mesh.userData.type === 'measurement') {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        // Annotations manage their own gizmo via labelContainer click
        if (mesh.userData.type === 'annotation') {
          transformControls.detach();
          return;
        }
        if (mesh.userData.lock_scale === undefined) {
          mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
        }

        if (mesh.userData.lock) {
          transformControls.detach(mesh);
          return;
        }

        if (!mesh.visible) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        if (selectionStore.selectedObjectTransformType) {
          // Don't attach transform controls for projectors with lockTarget in rotate mode
          if (
            selectionStore.selectedObjectTransformType === 'rotate' &&
            mesh.userData.isProjector &&
            mesh.userData.lockTarget
          ) {
            transformControls.detach();
            updateAllSpritesVisibility(false);
            return;
          }

          // Don't attach scale gizmo for projectors and LEDs
          if (selectionStore.selectedObjectTransformType === 'scale' && isScaleDisabledForType(mesh)) {
            transformControls.detach();
            updateAllSpritesVisibility(false);
            return;
          }

          const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');

          if (selectionStore.selectedObjectTransformTarget && targetMesh) {
            if (mesh.userData.lockTarget) return;
            isTransformingTarget = true;

            transformControls.attach(targetMesh);

            changeTransformControlsAll(selectionStore.selectedObjectTransformType, true);
          } else {
            isTransformingTarget = false;
            transformControls.attach(mesh);
            // setScaleGizmoMode(mesh, transformControls)
            changeTransformControlsAll(selectionStore.selectedObjectTransformType);
          }

          const gizmo = transformControls.getHelper();
          gizmo.traverse((object) => {
            object.layers.set(1);
            if ('isMesh' in object && object.isMesh) {
              object.userData.projectorOverlayIgnore = true;
            }
          });
          scene.add(gizmo);
          updateAllSpritesVisibility(true); // Hide sprites when in transform mode
        } else {
          updateAllSpritesVisibility(false); // Show sprites when not in transform mode
        }
      } else {
        transformControls.detach();
        updateAllSpritesVisibility(false); // Show sprites when nothing is selected
      }
    })
  );

  _watchStopHandles.push(
    watch([() => selectionStore.selectedObjectTransformType], ([newType]) => {
      if (selectionStore.selectedObjectId) {
        const mesh = getObjectByCode(selectionStore.selectedObjectId);
        if (!mesh) return;
        if (mesh.userData.lock) return;

        if (mesh.userData.lock_scale === undefined) {
          mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
        }

        if (mesh.userData.lock) {
          transformControls.detach(mesh);
          return;
        }

        if (!mesh.visible) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        const module = cruxModuleStore.cruxModules.find((m) => m.code === selectionStore.selectedObjectId);
        if (module) {
          mesh.userData.lock_scale = module.data.lock_scale;
        }
        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');

        if (newType) {
          // Don't attach transform controls for projectors with lockTarget in rotate mode
          if (newType === 'rotate' && mesh.userData.isProjector && mesh.userData.lockTarget) {
            transformControls.detach();
            updateAllSpritesVisibility(false);
            return;
          }

          // Don't attach scale gizmo for projectors
          if (newType === 'scale' && isScaleDisabledForType(mesh)) {
            transformControls.detach();
            updateAllSpritesVisibility(false);
            return;
          }

          if (selectionStore.selectedObjectTransformTarget && targetMesh) {
            if (mesh.userData.lockTarget) return;
            isTransformingTarget = true;

            transformControls.attach(targetMesh);
            changeTransformControlsAll(newType, true);
          } else {
            isTransformingTarget = false;
            transformControls.attach(mesh);
            // setScaleGizmoMode(mesh, transformControls)
            changeTransformControlsAll(newType);
          }

          const gizmo = transformControls.getHelper();
          gizmo.traverse((object) => {
            object.layers.set(1);
            if ('isMesh' in object && object.isMesh) {
              object.userData.projectorOverlayIgnore = true;
            }
          });
          scene.add(gizmo);
          updateAllSpritesVisibility(true); // Hide sprites when in transform mode
        } else {
          transformControls.detach();
          updateAllSpritesVisibility(false); // Show sprites when exiting transform mode
        }
      } else {
        transformControls.detach();
        updateAllSpritesVisibility(false); // Show sprites when nothing is selected
      }
      render();
    })
  );

  _watchStopHandles.push(
    watch([() => selectionStore.selectedObjectTransformTarget], ([newTargetMode]) => {
      if (selectionStore.selectedObjectId && selectionStore.selectedObjectTransformType) {
        const mesh = getObjectByCode(selectionStore.selectedObjectId);
        if (!mesh) return;
        if (mesh.userData.lock_scale === undefined) {
          mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
        }
        if (mesh.userData.lock) {
          transformControls.detach(mesh);
          return;
        }

        if (!mesh.visible) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        // Don't attach transform controls for projectors with lockTarget in rotate mode
        if (
          selectionStore.selectedObjectTransformType === 'rotate' &&
          mesh.userData.isProjector &&
          mesh.userData.lockTarget
        ) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        // Don't attach scale gizmo for projectors
        if (selectionStore.selectedObjectTransformType === 'scale' && isScaleDisabledForType(mesh)) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        // Block target mode if lockTarget is on
        if (newTargetMode && mesh.userData.lockTarget) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        // Target only supports translate
        if (newTargetMode && selectionStore.selectedObjectTransformType !== 'translate') {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        // Update the internal target transform state
        isTransformingTarget = newTargetMode;

        // Re-attach with the new mode
        transformControls.detach();

        if (newTargetMode) {
          const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');
          transformControls.attach(targetMesh);
          changeTransformControlsAll(selectionStore.selectedObjectTransformType, true);
        } else {
          transformControls.attach(mesh);
          // setScaleGizmoMode(mesh, transformControls)
          changeTransformControlsAll(selectionStore.selectedObjectTransformType, false);
        }

        const gizmo = transformControls.getHelper();
        gizmo.traverse((object) => {
          object.layers.set(1);
          if ('isMesh' in object && object.isMesh) {
            object.userData.projectorOverlayIgnore = true;
          }
        });
        scene.add(gizmo);
        render();
      }
    })
  );

  // Detach gizmo when lock or lockTarget is toggled ON while target gizmo is active
  _watchStopHandles.push(
    watch(
      () => {
        const id = selectionStore.selectedObjectId;
        if (!id) return undefined;
        const module = cruxModuleStore.cruxModules.find((m) => m.code === id);
        return { lock: module?.data?.lock, lockTarget: module?.data?.lockTarget };
      },
      (newVal) => {
        if (!newVal) return;
        const mesh = getObjectByCode(selectionStore.selectedObjectId);
        if (!mesh) return;

        // Sync to Three.js immediately so subsequent guards see the new value
        mesh.userData.lock = !!newVal.lock;
        mesh.userData.lockTarget = !!newVal.lockTarget;

        if (newVal.lock) {
          // Lock disables all gizmo interaction — detach entirely
          transformControls.detach();
          isTransformingTarget = false;
          selectionStore.selectedObjectTransformTarget = false;
          render();
        } else if (newVal.lockTarget && isTransformingTarget) {
          // lockTarget only detaches from target, reattaches to body
          isTransformingTarget = false;
          selectionStore.selectedObjectTransformTarget = false;
        }
      },
      { deep: true }
    )
  );
}

export function handleObjectLockChange(objectId, isLocked) {
  if (!transformControls) return;
  const selectionStore = useSelectionStore();

  if (objectId !== selectionStore.selectedObjectId) return;

  if (isLocked) {
    transformControls.detach();
    render();
  } else {
    const mesh = getObjectByCode(objectId);
    if (!mesh) return;

    const mode = selectionStore.selectedObjectTransformType || 'translate';
    selectionStore.selectedObjectTransformType = mode;

    const targetMesh = getObjectByCode(objectId + '-target');
    if (selectionStore.selectedObjectTransformTarget && targetMesh) {
      transformControls.attach(targetMesh);
      changeTransformControlsAll(mode, true);
    } else {
      transformControls.attach(mesh);
      changeTransformControlsAll(mode);
    }

    const gizmo = transformControls.getHelper();
    gizmo.traverse((object) => {
      object.layers.set(1);
      if ('isMesh' in object && object.isMesh) {
        object.userData.projectorOverlayIgnore = true;
      }
    });
    scene.add(gizmo);
    render();
  }
}

export function toggleTargetTransform() {
  isTransformingTarget = !isTransformingTarget;
  if (transformControls.object) {
    const selectionStore = useSelectionStore();
    const mesh = getObjectByCode(selectionStore.selectedObjectId);
    if (!mesh) return isTransformingTarget;

    transformControls.detach();
    if (isTransformingTarget && (mesh.target2 || mesh.parent.target2)) {
      transformControls.attach(mesh.target2 || mesh.parent.target2);
    } else {
      transformControls.attach(mesh);
      // setScaleGizmoMode(mesh, transformControls)
    }
  }
  return isTransformingTarget;
}

export function changeTransformControlsAll(type, target = false) {
  if (transformControls) {
    transformControls.setMode(type);
    transformControls.showX = true;
    transformControls.showY = true;
    transformControls.showZ = true;
    isTransformingTarget = target;
    if (transformControls.object) {
      const selectionStore = useSelectionStore();
      const mesh = getObjectByCode(selectionStore.selectedObjectId);
      if (!mesh) return;
      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }
      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      // Don't attach transform controls for projectors with lockTarget in rotate mode
      if (type === 'rotate' && mesh.userData.isProjector && mesh.userData.lockTarget) {
        transformControls.detach();
        return;
      }

      // Don't attach scale gizmo for projectors and LEDs
      if (type === 'scale' && isScaleDisabledForType(mesh)) {
        transformControls.detach();
        return;
      }

      transformControls.detach();
      if (isTransformingTarget) {
        // Target only supports translate
        if (type !== 'translate') {
          updateAllSpritesVisibility(false);
          return;
        }
        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');
        if (targetMesh) {
          if (mesh.userData.lockTarget) return;
          transformControls.attach(targetMesh);
        }
      } else {
        if (mesh.userData.lock_scale && type === 'scale') {
          transformControls.attach(mesh);
          setScaleGizmoMode(mesh, transformControls);
        } else {
          transformControls.attach(mesh);
        }
      }
    }
  }
}

export function changeTransformControls(type, axis, select, target = false) {
  if (transformControls) {
    const selectionStore = useSelectionStore();
    if (select) {
      selectionStore.selectedObjectTransformType = type;
    }

    transformControls.setMode(type);
    transformControls.showX = axis === 'x';
    transformControls.showY = axis === 'y';
    transformControls.showZ = axis === 'z';

    isTransformingTarget = target;
    if (transformControls.object) {
      const mesh = getObjectByCode(selectionStore.selectedObjectId);
      if (!mesh) return;
      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }
      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      // Don't attach transform controls for projectors with lockTarget in rotate mode
      if (type === 'rotate' && mesh.userData.isProjector && mesh.userData.lockTarget) {
        transformControls.detach();
        return;
      }

      // Don't attach scale gizmo for projectors and LEDs
      if (type === 'scale' && isScaleDisabledForType(mesh)) {
        transformControls.detach();
        return;
      }

      transformControls.detach();
      if (isTransformingTarget) {
        // Target only supports translate
        if (type !== 'translate') {
          updateAllSpritesVisibility(false);
          return;
        }
        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');
        if (targetMesh) {
          if (mesh.userData.lockTarget) return;
          transformControls.attach(targetMesh);
        }
      } else {
        transformControls.attach(mesh);
        // setScaleGizmoMode(mesh, transformControls)
      }
    }
  }
}

export function changeTransformControlsWorld(type) {
  if (transformControls) {
    transformControls.setSpace(type);
  }
}

export function cleanupTransformControls() {
  // Stop all Vue watchers
  _watchStopHandles.forEach((stop) => stop());
  _watchStopHandles = [];

  if (_onCameraChanged) {
    window.removeEventListener('cameraChanged', _onCameraChanged);
    _onCameraChanged = null;
  }

  // Remove named event listeners from transformControls
  if (transformControls) {
    if (_onChange) transformControls.removeEventListener('change', _onChange);
    if (_onDraggingChanged) transformControls.removeEventListener('dragging-changed', _onDraggingChanged);
    if (_onMouseDown) transformControls.removeEventListener('mouseDown', _onMouseDown);
    if (_onMouseUp) transformControls.removeEventListener('mouseUp', _onMouseUp);
    if (_onObjectChange) transformControls.removeEventListener('objectChange', _onObjectChange);
    transformControls.dispose();
  }

  _onChange = null;
  _onDraggingChanged = null;
  _onMouseDown = null;
  _onMouseUp = null;
  _onObjectChange = null;
}
